import toPairs from 'lodash/toPairs';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import { extractYouTubeID } from './string';

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

/**
 * Validates that a string is unique in an array of strings.
 * @param {Array<string>} stringArray - Array of strings to check against.
 * @param {Function} getMessage - Function that returns an error message.
 * @param {Function} toSlug - Function that converts a string to a slug.
 * @returns {string} - VALID if the string is unique, otherwise the error message.
 */
export const uniqueString = (currentIndex, stringArray, getMessage, toSlug) => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return getMessage('', '');
  }
  const slug = toSlug(value);
  const otherSlugs = stringArray.map(toSlug).filter((_, i) => i !== currentIndex);
  const isUnique = !otherSlugs.includes(slug);
  return isUnique ? VALID : getMessage(value, slug);
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const requiredSelectTreeOption = message => value => {
  if (typeof value === 'undefined' || value === null || Object.values(value)?.length === 0) {
    return message;
  }
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

// Detects URLs, www. prefixes and bare domains with common TLDs so we can
// reject phishing / SEO-backlink attempts in user-generated text.
const URL_LIKE_REGEX = /(\bhttps?:\/\/\S+)|(\bwww\.\S+)|(\b[a-z0-9-]{2,}\.(com|net|org|io|co|me|info|biz|shop|store|site|online|xyz|app|dev|ca|us|uk|ru|au|de|fr|cn|jp|in|br|mx|it|es|nl|pl|tr|kr|tv|ai|gg|to|link|click)\b)/i;

// Cyrillic / Greek / leetspeak look-alikes attackers use to bypass filters.
const HOMOGLYPHS_PRIMARY = {
  // Cyrillic
  а: 'a', А: 'a',
  в: 'b', В: 'b',
  с: 'c', С: 'c',
  е: 'e', Е: 'e', ё: 'e', Ё: 'e',
  ѕ: 's',
  і: 'i', І: 'i', ї: 'i', Ї: 'i',
  ј: 'j', Ј: 'j',
  к: 'k', К: 'k',
  м: 'm', М: 'm',
  н: 'h', Н: 'h',
  о: 'o', О: 'o',
  р: 'p', Р: 'p',
  т: 't', Т: 't',
  у: 'y', У: 'y',
  х: 'x', Х: 'x',
  // Greek
  α: 'a', Α: 'a',
  ο: 'o', Ο: 'o',
  ρ: 'p', Ρ: 'p',
  ν: 'v',
  τ: 't', Τ: 't',
  ι: 'i', Ι: 'i',
  // Leetspeak digits and symbols
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '6': 'g',
  '7': 't',
  '8': 'b',
  '9': 'g',
  '@': 'a',
  $: 's',
  '!': 'i',
  '|': 'i',
};

// Secondary mapping for characters that look like multiple letters. We run
// normalization twice — once with primary, once with secondary — so we catch
// both "1ocalflower" (1→l) / "ver1fy" (1→i) and "LocaIFlower" (i→l).
const HOMOGLYPHS_SECONDARY = {
  '1': 'l',
  i: 'l',
  l: 'i',
};

const normalizeWith = (value, primary, secondary) => {
  return String(value)
    .toLowerCase()
    .split('')
    .map(c => secondary[c] || primary[c] || c)
    .join('')
    .replace(/[^a-z0-9]/g, '');
};

// Returns the set of normalized variants for matching. Always includes the
// primary mapping; if any ambiguous char appears, also includes a variant
// using the secondary mapping for those chars.
const normalizeVariants = value => {
  if (!value) {
    return [];
  }
  const variants = new Set();
  variants.add(normalizeWith(value, HOMOGLYPHS_PRIMARY, {}));
  variants.add(normalizeWith(value, HOMOGLYPHS_PRIMARY, HOMOGLYPHS_SECONDARY));
  return Array.from(variants);
};

const normalizeForMatch = value => normalizeVariants(value)[0] || '';

const matchesAnyKeyword = (value, normalizedKeywords) => {
  const variants = normalizeVariants(value);
  return variants.some(v => normalizedKeywords.some(kw => v.includes(kw)));
};

export const noLinks = message => value => {
  if (!value) {
    return VALID;
  }
  return URL_LIKE_REGEX.test(value) ? message : VALID;
};

// Reject anything that is not ASCII letters / digits / a small set of
// punctuation. Used on user-visible name fields to block Cyrillic, Greek,
// math-alphanumeric and other Unicode tricks attackers use to bypass keyword
// filters. Allowed: a-z, A-Z, 0-9, space, hyphen, apostrophe, period,
// ampersand, comma.
const ENGLISH_ONLY_REGEX = /^[a-zA-Z0-9\s\-'.&,]+$/;

export const englishOnly = message => value => {
  if (!value) {
    return VALID;
  }
  return ENGLISH_ONLY_REGEX.test(value) ? VALID : message;
};

// More permissive variant for free-form text fields (bio, listing description,
// chat messages). Allows all printable ASCII plus tabs and newlines plus emoji.
// Also allows common typographic punctuation that word processors auto-substitute
// for ASCII equivalents: smart quotes ‘ ’ “ ”, en/em dashes – —, ellipsis …,
// non-breaking space, ° © ® ™. Still blocks Cyrillic, Greek, Chinese,
// math-alphanumeric and other Unicode tricks.
// 0x09–0x0D = tab/LF/VT/FF/CR, 0x20–0x7E = printable ASCII.
const ENGLISH_TEXT_REGEX = /^[\x09-\x0D\x20-\x7E ©®°·‐-―‘’“”•…′″™\p{Extended_Pictographic}‍️]+$/u;

export const englishText = message => value => {
  if (!value) {
    return VALID;
  }
  return ENGLISH_TEXT_REGEX.test(value) ? VALID : message;
};

// Reject names that try to impersonate marketplace staff or admin roles.
const IMPERSONATION_KEYWORDS = [
  'localflower',
  'local flower',
  'support',
  'admin',
  'administrator',
  'moderator',
  'verification',
  'verify',
  'official',
  'staff',
  'helpdesk',
  'help desk',
  'security',
];

const NORMALIZED_IMPERSONATION_KEYWORDS = IMPERSONATION_KEYWORDS.map(normalizeForMatch);

export const noImpersonation = message => value => {
  if (!value) {
    return VALID;
  }
  return matchesAnyKeyword(value, NORMALIZED_IMPERSONATION_KEYWORDS) ? message : VALID;
};

// Reject messages that contain banned phrases. Matching is homoglyph-aware
// and ignores spacing / punctuation so "S.u.p.р.o.r.t" still gets caught.
export const noBannedPhrases = (message, phrases) => value => {
  if (!value || !phrases || phrases.length === 0) {
    return VALID;
  }
  const normalizedPhrases = phrases.map(normalizeForMatch);
  return matchesAnyKeyword(value, normalizedPhrases) ? message : VALID;
};

// True if the concatenated combination of name parts matches a blocked
// impersonation keyword. Catches splits like first="Local" last="Flower",
// homoglyph attacks like first="Supрort" (Cyrillic р) last="L", and
// leetspeak like first="SUPP0RT" or first="1ocalflower" (digit 1 for l).
export const hasImpersonationInParts = (...parts) => {
  const combined = parts.filter(Boolean).join(' ');
  if (!combined) {
    return false;
  }
  return matchesAnyKeyword(combined, NORMALIZED_IMPERSONATION_KEYWORDS);
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const validateInteger = (value, max, min, numberTooSmallMessage, numberTooBigMessage) => {
  const parsedValue = Number.parseInt(value, 10);
  if (parsedValue > max) {
    return numberTooBigMessage;
  }
  if (parsedValue < min) {
    return numberTooSmallMessage;
  }
  return VALID;
};

// If URL is passed to this function as null, will return VALID
export const validateYoutubeURL = (url, message) => {
  return url ? (extractYouTubeID(url) ? VALID : message) : VALID;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);
